import { FC, PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react';
import useIsHydrated from '../../lib/utils/useIsHydrated';
import { getAdobeFlowName, onlyNumberKey } from '../../lib/utils/utils';
import formStyles from '../../components/form/form.module.scss';
import readRxModalstyles from './readRxModal.module.scss';
import styles from './rxlabel.module.scss';
import { clickAnalytics, formErrorAnalytics, tags } from '../../lib/utils/analytics';
import { Input, Link, Modal } from '@digital-pulse/react';
import DateFieldsetWrapper from '@/components/dateFieldset/DateFieldsetWrapper';
import DeprecatedMapPin from '../../public/images/deprecatedMapPin.svg';
import OpenPopUp from '../../public/images/openPopUp.svg';
import RxBottle from '../../public/images/rxBottle.svg';
import RxLabel from '../../public/images/rxLabel.svg';
import ExtLink from '../../public/images/extLink.svg';
import DividerVertical from '../../public/images/dividerVertical.svg';
import RxBottle2 from '../../public/images/rxBottle2.svg';
import Convo from '../../public/images/convo.svg';

interface Hours {
  DayHours?: DayHour[];
}

interface DayHour {
  Day?: string;
  Hours?: string;
}
interface V3Location {
  StoreNumber?: string;
  minuteClinicID?: string;
  opticalClinicID?: string;
  storeType?: number;
  pharmacyNCPDPProviderIdentifier?: string;
  addressLine?: string;
  addressCityDescriptionText?: string;
  addressState?: string;
  addressZipCode?: string;
  addressCountry?: string;
  geographicLatitudePoint?: string;
  geographicLongitudePoint?: string;
  indicatorStoreTwentyFourHoursOpen?: string;
  indicatorPrescriptionService?: string;
  indicatorPhotoCenterService?: string;
  indicatorMinuteClinicService?: string;
  indicatorOpticalService?: string;
  instorePickupService?: string;
  indicatorDriveThruService?: string;
  indicatorPharmacyTwentyFourHoursOpen?: string;
  rxConvertedFlag?: string;
  indicatorCircularConverted?: string;
  indicatorH1N1FluShot?: string;
  indicatorRxFluFlag?: string;
  indicatorWicService?: string;
  snapIndicator?: string;
  indicatorVaccineServiceSupport?: string;
  indicatorPneumoniaShotService?: string;
  indicatorWeeklyAd?: string;
  indicatorCVSStore?: string;
  indicatorStorePickup?: string;
  storeLocationTimeZone?: string;
  storePhonenumber?: string;
  pharmacyPhonenumber?: string;
  storeHours?: Hours;
  pharmacyHours?: Hours;
  adVersionCdCurrent?: string;
  adVersionCdNext?: string;
}
interface Props {
  formStatus?: any;
  allowYearOnlyForPetLookup?: boolean;
  hasShowHide?: boolean;
  label?: string;
  helper?: string;
  showStoreLocator?: boolean;
  englishMessageMap?: Record<string, string>;
  // Translated text props
  readRxLabelInfo?: string;
  allFieldsRequired?: string;
  storeLocator?: string;
  rxLabelFormLabels?: {
    STORE_NUMBER?: string;
    PRESCRIPTION_NUMBER?: string;
  };
  rxLabelFormModalText?: {
    HEADER?: string;
    LEFT_SUB_HEAD?: string;
    LEFT_DESC?: string;
    RIGHT_SUB_HEAD?: string;
    RIGHT_DESC?: string;
    LEFT_LINK_DESC?: string;
    RIGHT_LINK_DESC?: string;
    RXLABEL_LINK?: string;
    SPOKEN_LINK?: string;
  };
  analytics?: { flowName: string; pageName: string };
}

const RxLabelLookup: FC<PropsWithChildren<Props>> = ({
  formStatus,
  allowYearOnlyForPetLookup,
  hasShowHide,
  label,
  helper,
  showStoreLocator,
  readRxLabelInfo,
  allFieldsRequired,
  storeLocator,
  rxLabelFormLabels,
  rxLabelFormModalText,
  englishMessageMap,
  analytics
}: Props): ReactElement => {
  const { register, formState, setValue } = formStatus;
  const isHydrated = useIsHydrated();
  const modalRef = useRef(null);
  const [isRxModalOpen, setRxModalOpen] = useState(false);
  const [isStoreModalOpen, setStoreModalOpen] = useState(false);
  const dobInputRef = useRef(null);
  const storeInputRef = useRef(null);
  const { errors } = formState;

  useEffect(() => {
    const error_keys = Object.keys(errors);
    if (error_keys?.length > 0) {
      formErrorAnalytics(
        error_keys,
        errors,
        analytics?.flowName || getAdobeFlowName(),
        analytics?.pageName || tags.enterYourInformationPage,
        'search',
        undefined,
        englishMessageMap
      );
    }
  }, [errors]);

  const handleFavoriteStoreUpdate = (ev: CustomEvent<V3Location>) => {
    if (ev?.detail?.StoreNumber) {
      setValue('storeNumber', ev.detail.StoreNumber);
    }
    // Only close modal if it was actually opened by user interaction
    if (isStoreModalOpen) {
      toggleStoreModal(false);
    }
  };

  useEffect(() => {
    document.addEventListener('cvsHeaderFavStoreUpdated', handleFavoriteStoreUpdate);
    return () => {
      document.removeEventListener('cvsHeaderFavStoreUpdated', handleFavoriteStoreUpdate);
    };
  }, [isStoreModalOpen]);

  const toggleStoreModal = (setModalOpen: boolean) => {
    setStoreModalOpen(setModalOpen);
    window?.dispatchEvent(
      new CustomEvent('cvsHeaderModalToggle', {
        detail: setModalOpen
      })
    );
  };

  const changeStoreHandler = () => {
    const data = {
      payload: {
        link_name: `custom:account:self_serve:rx_label_page:look_up_store`
      }
    };
    clickAnalytics(data);
    toggleStoreModal(true);
  };

  const handleSelectedStoreUpdate = (event) => {
    setValue('storeNumber', event?.target?.value);
  };

  const handleReadRxLabelShowModal = () => {
    setRxModalOpen(true);
    const data = {
      payload: {
        link_name: `custom:account:self_serve:rx_label_page:how_to_read_rx_label`
      }
    };
    clickAnalytics(data);
  };

  if (!isHydrated) {
    return null;
  }

  return (
    <div className="form_page_container">
        <Link
          className={styles.read_rxlabel}
          href="#"
          variant="inline"
          onClick={handleReadRxLabelShowModal}
          renderTrailing={<OpenPopUp className={styles.read_rxlabel__icon} aria-hidden={true} />}
        >
          {readRxLabelInfo}
        </Link>
        <div className={formStyles.form__info_text}>{allFieldsRequired}</div>
        {allowYearOnlyForPetLookup ? (
          <Input
            isErrorAnnounced={false}
            id={styles.dateOfBirth}
            className="yearOnly"
            pattern="\d{4}"
            maxLength={4}
            onKeyDown={(e) => onlyNumberKey(e)}
            size={4}
            inputMode="numeric"
            autoComplete="off"
            min={1900}
            max={new Date().getFullYear()}
            ref={dobInputRef}
            label={label}
            helper={helper}
            error={errors.dateOfBirth?.message?.toString()}
            data-test-id="dateOfBirth"
            {...register('dateOfBirth')}
          />
        ) : (
          <DateFieldsetWrapper
            refProp={dobInputRef}
            error={errors.dateOfBirth?.message?.toString()}
            dataTestId="dateOfBirth"
            register={register}
            hasShowHide={hasShowHide}
            isShowHideChecked={true}
          />
        )}
        <div className={styles.store_locator_container}>
          <Input
            id={styles.storeNumber}
            onKeyDown={(e) => onlyNumberKey(e)}
            onInput={handleSelectedStoreUpdate}
            ref={storeInputRef}
            label={rxLabelFormLabels?.STORE_NUMBER}
            error={errors.storeNumber?.message?.toString()}
            data-test-id="storeNumber"
            isErrorAnnounced={false}
            {...register('storeNumber')}
            maxLength={5}
            className={styles.store_link}
          />
          {showStoreLocator && (
            <Link
              id="storeLocatorLink"
              href="#"
              renderLeading={<DeprecatedMapPin aria-hidden={true} />}
              variant="inline"
              onClick={changeStoreHandler}
              subtype="high-contrast"
            >
              {storeLocator}
            </Link>
          )}
        </div>
        <Input
          id={styles.prescriptionNumber}
          onKeyDown={(e) => onlyNumberKey(e)}
          label={rxLabelFormLabels?.PRESCRIPTION_NUMBER}
          error={errors.prescriptionNumber?.message?.toString()}
          data-test-id="prescriptionNumber"
          isErrorAnnounced={false}
          inputMode="numeric"
          {...register('prescriptionNumber')}
          maxLength={7}
        ></Input>

        <Modal
          heading={rxLabelFormModalText?.HEADER}
          ref={modalRef}
          className="read_rxlabel_modal"
          size="lg"
          role="dialog"
          aria-modal="true"
          isOpen={isRxModalOpen}
          onClose={() => setRxModalOpen(false)}
        >
          <div
            className={readRxModalstyles['rx-modal']}
            role="dialog"
            aria-label="prescription content"
          >
            <div className={readRxModalstyles['rx-label-div']}>
              <RxBottle aria-hidden={true} />
              <div className={readRxModalstyles.spacer} />
              <RxLabel className={readRxModalstyles['example-label']} aria-hidden={true} />
              <h3>{rxLabelFormModalText?.LEFT_SUB_HEAD}</h3>
              <p>{rxLabelFormModalText?.LEFT_DESC}</p>
              <Link
                id="readRxLabelLink"
                className={readRxModalstyles['rx-modal__link']}
                variant="inline"
                href={rxLabelFormModalText?.RXLABEL_LINK}
                renderTrailing={<ExtLink className={readRxModalstyles['rx-modal__link']} />}
              >
                {rxLabelFormModalText?.LEFT_LINK_DESC}
              </Link>
            </div>
            <div className={readRxModalstyles['rx-modal__divider-container']} aria-hidden="true">
              <DividerVertical className={readRxModalstyles['rx-modal__divider-line']} />
            </div>
            <div className={readRxModalstyles['rx-modal__rx-talk-div']}>
              <div aria-hidden className={readRxModalstyles['rx-modal__talking']}>
                <RxBottle2 />
                <div className={readRxModalstyles.spacer}></div>
                <div className={readRxModalstyles['rx-modal__talking__convo-container']}>
                  <Convo />
                </div>
              </div>
              <h3>{rxLabelFormModalText?.RIGHT_SUB_HEAD}</h3>
              <p>{rxLabelFormModalText?.RIGHT_DESC}</p>
              <div className={readRxModalstyles['link-container']}>
                <Link
                  className={readRxModalstyles['link-container__grey-link']}
                  variant="inline"
                  href={rxLabelFormModalText?.SPOKEN_LINK}
                  renderTrailing={<ExtLink className="link-container__logo" />}
                >
                  {rxLabelFormModalText?.RIGHT_LINK_DESC}
                  <div className={readRxModalstyles.spacer}></div>
                </Link>
              </div>
            </div>
          </div>
        </Modal>
      </div>
  );
};

export default RxLabelLookup;
